import { AfterViewInit, ChangeDetectorRef, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NetzbetreiberService } from '../../service/netzbetreiber/netzbetreiber.service';
import { Netzbetreiber } from '../../domain/model/netzbetreiber';
import { NetzbetreiberDataSource } from './netzbetreiber-data-source';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { RoutingService } from '../../service/common/routing/routing.service';
import { ActivatedRoute, Router } from '@angular/router';
import { map, shareReplay, skip, startWith, takeWhile, tap } from 'rxjs/operators';
import { DienstleistungsmoduleService } from '../../service/dienstleistungsmodule/dienstleistungsmodule.service';
import {
    combineLatest,
    distinctUntilChanged,
    filter,
    merge,
    Observable,
    ReplaySubject,
    Subject,
    switchMap,
} from 'rxjs';
import { DienstleistungsModuleBeschreibung } from '../../domain/model/dienstleistungsmodulebeschreibung.model';
import { NetzbetreiberFilter } from '../../service/netzbetreiber/netzbetreiber-filter';
import { Pageable } from '../../common/paging/pageable';
import { distinctUntilFilterChanged } from '../../common/util/helper';

@Component({
    selector: 'redispatch-netzbetreiber-list',
    templateUrl: './netzbetreiber-list.component.html',
    styleUrls: [
        './netzbetreiber-list.component.scss',
    ],
    standalone: false,
})
export class NetzbetreiberListComponent implements OnInit, AfterViewInit, OnDestroy {

    public readonly PAGE_SIZE = 50;

    public netzbetreiberList: NetzbetreiberDataSource = new NetzbetreiberDataSource();
    public page$: Observable<Pageable>;
    public filter$: Observable<NetzbetreiberFilter>;

    @ViewChild(MatSort, { static: false }) sort: MatSort;
    @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;

    private pageFromUserInteraction$: Observable<Pageable>;
    private pageEventFromUserInteraction$ = new ReplaySubject<PageEvent>(1);
    private sortFromUserInteraction$ = new ReplaySubject<Sort>(1);
    private filterFromUserInteraction$ = new Subject<NetzbetreiberFilter>();
    private dienstleistungsModuleBeschreibungen$: Observable<DienstleistungsModuleBeschreibung[]>;

    private netzbetreiber: Netzbetreiber[] = [];
    private currentPageIndex = 0;
    private componentDestroyed = false;

    public columnsToDisplay = [
        'name',
        'teilgebiet',
        'netzbetreibernummer',
        'stadtwerkeId',
        'geschaeftspartnerId',
        'lastImportDate',
        'dienstleistungsart',
        'dienstleistungsmoduleBeschreibung',
    ];

    public routingService = inject(RoutingService);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private routeService = inject(RoutingService);
    private netzbetreiberService = inject(NetzbetreiberService);
    private dienstleistungsmoduleService = inject(DienstleistungsmoduleService);
    private changeDetectorRef = inject(ChangeDetectorRef);

    constructor() {
        this.dienstleistungsModuleBeschreibungen$ =
            this.dienstleistungsmoduleService.listDienstleistungsModuleBeschreibung()
                .pipe(
                    shareReplay(1),
                    takeWhile(() => !this.componentDestroyed),
                );
    }

    ngOnInit() {
        this.setupFilterObservable();
        this.setupPageObservable();
        this.setupReloadObservable();
    }

    ngAfterViewInit() {
        this.netzbetreiberList.sort = this.sort;
        this.netzbetreiberList.paginator = this.paginator;
    }

    ngOnDestroy(): void {
        this.componentDestroyed = true;
    }

    private setupPageObservable() {
        this.pageFromUserInteraction$ = combineLatest([
            this.sortFromUserInteraction$,
            this.pageEventFromUserInteraction$,
        ])
            .pipe(
                map(params => {
                    const pageable = new Pageable();

                    const sort = params[0];
                    const page = params[1];

                    pageable.sort = sort?.active || '';
                    pageable.direction = sort?.direction || '';
                    pageable.page = page?.pageIndex || 0;

                    return pageable;
                }),
                shareReplay(1),
            );

        this.route.queryParams
            .pipe(
                takeWhile(() => !this.componentDestroyed),
            )
            .subscribe(params => {
                this.sortFromUserInteraction$.next({
                    active: params['sort'],
                    direction: params['direction'],
                });
                this.pageEventFromUserInteraction$.next({
                    pageIndex: +params['page'],
                } as PageEvent);
            });

        this.page$ = this.pageFromUserInteraction$
            .pipe(
                distinctUntilFilterChanged(),
                tap(page => {
                    this.currentPageIndex = page.page;
                    this.netzbetreiberList.data = [];  // Clear first
                    this.changeDetectorRef.markForCheck();
                    setTimeout(() => {
                        this.netzbetreiberList.data = this.netzbetreiber;
                        this.changeDetectorRef.markForCheck();
                    });
                }),
                shareReplay(1),
            );
    }

    private setupReloadObservable(): void {
        // Goto first page, when filter changes
        this.filterFromUserInteraction$
            .pipe(
                skip(1),
                takeWhile(() => !this.componentDestroyed),
            )
            .subscribe(_filter => {
                const page = new PageEvent();
                page.pageIndex = 0;
                this.pageEventFromUserInteraction$.next(page);
            });

        combineLatest([
            this.pageFromUserInteraction$,
            this.filterFromUserInteraction$,
        ])
            .pipe(
                filter(() => {
                    const currentParams = this.route.snapshot.queryParams;
                    return !currentParams['openedInSideNav'];
                }),
                map(param => {
                    const pageable = param[0];
                    const nbFilter = param[1];
                    return this.routeService.netzbetreiberTable(pageable, nbFilter);
                }),
                distinctUntilChanged(),
                takeWhile(() => !this.componentDestroyed),
            )
            .subscribe(url => {
                this.router.navigateByUrl(url);
            });

        this.netzbetreiberService.refreshRequested$
            .pipe(
                startWith(null),
                takeWhile(() => !this.componentDestroyed),
                switchMap(() => this.netzbetreiberService.getAlleNetzbetreiber()),
                switchMap(netzbetreiber => {
                    return this.dienstleistungsModuleBeschreibungen$.pipe(
                        map(modules => {
                            netzbetreiber.forEach(n => {
                                n.dienstleistungsmoduleBeschreibung = NetzbetreiberListComponent.resolveModuleDescription(n, modules);
                            });
                            return netzbetreiber;
                        }),
                    );
                }),
            )
            .subscribe(netzbetreiber => {
                this.netzbetreiberList.paginator.pageIndex = this.currentPageIndex;
                this.netzbetreiberList.data = netzbetreiber;
                this.netzbetreiber = netzbetreiber;
                this.changeDetectorRef.markForCheck();
            });
    }

    private static resolveModuleDescription(
        netzbetreiber: Netzbetreiber,
        moduleBeschreibung: DienstleistungsModuleBeschreibung[],
    ): DienstleistungsModuleBeschreibung[] {
        return netzbetreiber.dienstleistungsmodule.map(module => {
            return moduleBeschreibung.find(value => value.dienstleistungsmodule === module);
        });
    }

    private setupFilterObservable() {
        const filterFromQueryParam$ = this.route.queryParams
            .pipe(
                map((params: any) => {
                    const nbFilter = new NetzbetreiberFilter();
                    if (params) {
                        nbFilter.name = params['name'];
                        nbFilter.stwId = params['stwId'];
                        nbFilter.teilgebiet = params['teilgebiet'];
                        nbFilter.netzbetreibernummer = params['netzbetreibernummer'];
                        nbFilter.geschaeftspartnerId = params['geschaeftspartnerId'];
                        nbFilter.lastImportDate = params['lastImportDate'] ? JSON.parse(params['lastImportDate']) : null;
                        nbFilter.dienstleistungsart = params['dienstleistungsart'];
                        nbFilter.dienstleistungsmoduleBeschreibung = params['dienstleistungsmodule'] ? JSON.parse(params['dienstleistungsmodule']) : null;
                    }
                    return nbFilter;
                }),
            );

        this.filter$ = merge(
            this.filterFromUserInteraction$,
            filterFromQueryParam$,
        ).pipe(
            distinctUntilFilterChanged(),
            shareReplay(1),
        );
    }

    filterChanged(nbFilter: NetzbetreiberFilter) {
        this.filterFromUserInteraction$.next(nbFilter);
        this.netzbetreiberList.filter = JSON.stringify(nbFilter);
        this.changeDetectorRef.markForCheck();
    }

    pageChange($event: PageEvent) {
        this.pageEventFromUserInteraction$.next($event);
    }

    sortChange($event: Sort) {
        if ($event.direction === '') {
            $event.active = '';
        }
        this.sortFromUserInteraction$.next($event);
    }
}
